import { ImagePlugin } from '../../../capacitor_plugins/image';
import { withPayloadLogging } from '../../capacitor/capacitorPayloadLogging';
import { CAPACITOR_IMAGE_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { MNElementMap } from '../../../common/elements/elementModelTypes';
import globalLogger from '../../logger';

const logger = globalLogger.createChannel('element.image.sendToCapacitor');

/**
 * Splits a large image element map into multiple JSON string chunks, each not exceeding the specified byte size
 * This is only applied to image path/names/etc and not the bytes of the image itself
 *
 * @param imagesObj - An MNElementMap
 * @param maxBytes - The maximum UTF-8 byte size for each chunk (default of 500KB)
 * @returns An array of JSON string chunks, each representing a subset of the original images object
 */
function chunkImageObjectByBytes(imagesObj: MNElementMap, maxBytes = 500 * 1024): string[] {
    if (!imagesObj || typeof imagesObj !== 'object') return [];
    const entries = Object.entries(imagesObj);
    if (entries.length === 0) return [];

    const encoder = new TextEncoder();
    const chunks = [];
    let currentPairs = [];
    let currentSize = 2;

    for (const [elementId, imageObject] of entries) {
        const imageObjectJson = JSON.stringify(imageObject);
        const elementIdJson = JSON.stringify(elementId);
        const pair = elementIdJson + ':' + imageObjectJson;
        const pairLength = encoder.encode(pair).length;

        const projected = currentSize + pairLength + (currentPairs.length ? 1 : 0);
        if (projected > maxBytes && currentPairs.length) {
            chunks.push('{' + currentPairs.join(',') + '}');
            currentPairs = [];
            currentSize = 2;
        }
        currentPairs.push(pair);
        currentSize += pairLength + (currentPairs.length > 1 ? 1 : 0);
    }

    if (currentPairs.length) {
        chunks.push('{' + currentPairs.join(',') + '}');
    }
    return chunks.length ? chunks : [];
}

/**
 * Native carousel note:
 * When opening the native (Capacitor) image carousel we gather all board images, JSON‑serialize them and now CHUNK
 * that payload (see chunkImageObjectByBytes) into <= ~500KB pieces to avoid exceeding native bridge limits. If only
 * one chunk is produced we send it directly; otherwise we prime, append chunks, then present the carousel.
 */
export const sendImagesToCapacitor = async (imagesObject: MNElementMap, elementId: string, onClosed: () => void) => {
    let chunkedImageObjects;
    try {
        chunkedImageObjects = chunkImageObjectByBytes(imagesObject);
    } catch (error) {
        logger.error('Failed to send images to Capacitor', error);
        return;
    }

    if (chunkedImageObjects.length === 0) {
        logger.error('Failed to chunk image data');
        return;
    }

    const listener = await ImagePlugin.addListener('imageCarouselDidClose', () => {
        listener.remove();
        onClosed();
    });

    await withPayloadLogging(
        `${CAPACITOR_IMAGE_PLUGIN}.prepareImageCarousel`,
        (payload) => ImagePlugin.prepareImageCarousel(payload),
        { totalChunks: chunkedImageObjects.length },
    );
    for (let i = 0; i < chunkedImageObjects.length; i++) {
        await withPayloadLogging(
            `${CAPACITOR_IMAGE_PLUGIN}.appendImageCarouselChunk`,
            (payload) => ImagePlugin.appendImageCarouselChunk(payload),
            {
                chunkIndex: i,
                chunk: chunkedImageObjects[i],
            },
        );
    }
    withPayloadLogging(
        `${CAPACITOR_IMAGE_PLUGIN}.presentImageCarousel`,
        (payload) => ImagePlugin.presentImageCarousel(payload),
        { currentImageId: elementId },
    );
};
